import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { LogOut, Clock, Users, CalendarCheck, ClipboardList, BookOpen } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import NormsModal from '@/components/NormsModal';
import { ACTIVITY_NORMS, FREE_ACCESS_ACTIVITIES, FREE_ACCESS_NORMS } from '@/data/activityNorms';

export default function FamilyDashboard() {
  const { user, logout, api } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState([]);
  const [dayType, setDayType] = useState('A');
  const [family, setFamily] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState('today');
  const [normsOpen, setNormsOpen] = useState(null); // { title, norms }

  const fetchData = useCallback(async () => {
    try {
      const [actRes, famRes, resRes] = await Promise.all([
        api.get('/activities-today'),
        api.get('/families/me'),
        api.get('/reservations'),
      ]);
      setWorkshops(actRes.data.workshops || []);
      setDayType(actRes.data.day_type || 'A');
      setFamily(famRes.data);
      setReservations(resRes.data || []);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => { logout(); navigate('/'); };

  const sorted = [...workshops].sort((a, b) => a.time_start.localeCompare(b.time_start));

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="family-dashboard">
      <header className="bg-white border-b border-black/5 px-4 sm:px-8 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-9 w-auto" />
            <div>
              <p className="text-xs text-manrique-grey">{t('welcome')}, {family?.family_name || user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" />
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-manrique-grey hover:text-manrique-black transition-colors" data-testid="logout-button">
              <LogOut size={16} /> {t('logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-5">
        {/* Two buttons */}
        <div className="flex gap-3" data-testid="family-tabs">
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold transition-all ${
              activeTab === 'today' ? 'bg-[#48293A] text-white shadow-lg' : 'bg-white text-manrique-grey border border-black/10'
            }`}
            data-testid="tab-today"
          >
            <CalendarCheck size={18} /> Actividades de Hoy
          </button>
          <button
            onClick={() => setActiveTab('registered')}
            className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-bold transition-all ${
              activeTab === 'registered' ? 'bg-[#48293A] text-white shadow-lg' : 'bg-white text-manrique-grey border border-black/10'
            }`}
            data-testid="tab-registered"
          >
            <ClipboardList size={18} /> Mis Inscripciones
            {reservations.length > 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeTab === 'registered' ? 'bg-white/20' : 'bg-[#48293A]/10 text-[#48293A]'}`}>
                {reservations.length}
              </span>
            )}
          </button>
        </div>

        {/* TAB: Actividades de Hoy */}
        {activeTab === 'today' && (
          <>
            {sorted.length === 0 ? (
              <div className="card-manrique text-center py-12 text-manrique-muted">
                <CalendarCheck size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{t('no_workshops_day')}</p>
              </div>
            ) : (
              <div className="space-y-5">
                {sorted.map(a => {
                  const isRequired = a.registration_required;
                  const spotsLeft  = a.max_capacity - a.current_count;
                  const normsData  = ACTIVITY_NORMS[a.title];
                  return (
                    <div key={a.id} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-white" data-testid={`dashboard-activity-${a.id}`}>
                      <div className={`px-5 py-5 ${isRequired ? 'bg-gradient-to-br from-[#48293A] to-[#844E33]' : 'bg-gradient-to-br from-[#27AE60] to-[#1E8C4E]'}`}>
                        <div className="flex items-start justify-between">
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/20 text-white">
                            {isRequired ? 'Registro obligatorio' : 'Acceso libre'}
                          </span>
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/90 text-manrique-black">
                            {spotsLeft > 0 ? `${spotsLeft} plazas` : 'Completo'}
                          </span>
                        </div>
                        <h3 className="font-heading font-bold text-2xl text-white mt-3">{a.title}</h3>
                      </div>
                      <div className="px-5 py-4 space-y-3">
                        <p className="text-sm text-manrique-grey">{a.description}</p>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs border border-black/10 rounded-full px-3 py-1 text-manrique-black font-medium">
                            {a.age_min}-{a.age_max} {t('years')}
                          </span>
                          <span className={`text-xs rounded-full px-3 py-1 font-medium ${isRequired ? 'bg-[#48293A]/10 text-[#48293A]' : 'bg-[#27AE60]/10 text-[#27AE60]'}`}>
                            {a.time_start} - {a.time_end}
                          </span>
                        </div>
                        {normsData && (
                          <div className="flex flex-wrap gap-1.5">
                            {normsData.stats.slice(0, 3).map(s => (
                              <span key={s.label} className="text-[10px] bg-black/5 text-manrique-black px-2 py-0.5 rounded-full">
                                {s.label}: <strong>{s.value}</strong>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          {!isRequired && (
                            <div className="bg-[#A8752B]/8 border border-[#A8752B]/20 rounded-xl px-4 py-3 flex-1">
                              <p className="text-xs text-[#A8752B] leading-relaxed">
                                <span className="font-bold">Por orden de llegada.</span> Los menores de 9 años deben estar acompañados de un adulto durante la actividad.
                              </p>
                            </div>
                          )}
                          {isRequired && !normsData && <div className="flex-1" />}
                          {normsData && (
                            <button
                              onClick={() => setNormsOpen({ title: a.title, norms: normsData })}
                              className="flex items-center gap-1.5 text-sm font-medium text-[#48293A] border border-[#48293A]/20 px-3 py-1.5 rounded-2xl hover:bg-[#48293A]/5 transition-colors ml-3 flex-shrink-0"
                            >
                              <BookOpen size={14} /> Ver normas
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Actividades de acceso libre */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-black/8" />
                <p className="text-xs font-semibold text-manrique-muted uppercase tracking-widest whitespace-nowrap">Acceso libre todo el día</p>
                <div className="flex-1 h-px bg-black/8" />
              </div>
              {FREE_ACCESS_ACTIVITIES.map(act => {
                const norms = FREE_ACCESS_NORMS[act.normsKey];
                return (
                  <div key={act.id} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-white">
                    <div className="bg-gradient-to-br from-[#27AE60] to-[#1E8C4E] px-5 py-4">
                      <div className="flex items-start justify-between">
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/20 text-white">Acceso libre</span>
                      </div>
                      <h3 className="font-heading font-bold text-xl text-white mt-2">{act.title}</h3>
                      <p className="text-white/70 text-xs mt-1">{act.subtitle}</p>
                    </div>
                    {norms && (
                      <div className="px-5 py-4 space-y-3">
                        <div className="flex flex-wrap gap-1.5">
                          {norms.stats.slice(0, 3).map(s => (
                            <span key={s.label} className="text-[10px] bg-black/5 text-manrique-black px-2 py-0.5 rounded-full">
                              {s.label}: <strong>{s.value}</strong>
                            </span>
                          ))}
                        </div>
                        <button
                          onClick={() => setNormsOpen({ title: act.title, norms })}
                          className="flex items-center gap-1.5 text-sm font-medium text-[#48293A] border border-[#48293A]/20 px-3 py-1.5 rounded-2xl hover:bg-[#48293A]/5 transition-colors"
                        >
                          <BookOpen size={14} /> Ver normas
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* TAB: Mis Inscripciones */}
        {activeTab === 'registered' && (
          <>
            {reservations.length === 0 ? (
              <div className="card-manrique text-center py-12 text-manrique-muted">
                <ClipboardList size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No tienes inscripciones registradas</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reservations.map(r => (
                  <div key={r.id} className="rounded-3xl overflow-hidden border border-black/5 shadow-sm bg-white" data-testid={`my-reservation-${r.id}`}>
                    <div className="bg-gradient-to-br from-[#48293A] to-[#844E33] px-5 py-4">
                      <div className="flex items-start justify-between">
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/20 text-white">Inscrito</span>
                        {r.plaza_number && (
                          <span className="text-xs font-bold px-3 py-1 rounded-full bg-white/90 text-[#48293A]">
                            Plaza #{r.plaza_number}
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading font-bold text-xl text-white mt-2">{r.workshop_title}</h3>
                    </div>
                    <div className="px-5 py-4 space-y-2">
                      <div className="flex flex-wrap gap-2 text-xs">
                        {r.workshop_date && (
                          <span className="border border-black/10 rounded-full px-3 py-1 text-manrique-black font-medium">{r.workshop_date}</span>
                        )}
                        {r.workshop_time_start && (
                          <span className="bg-[#48293A]/10 text-[#48293A] rounded-full px-3 py-1 font-medium">
                            {r.workshop_time_start} - {r.workshop_time_end}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-manrique-grey">
                        {(r.children?.map(c => c.name) ?? r.children_names)?.join(', ')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <NormsModal
        open={!!normsOpen}
        onClose={() => setNormsOpen(null)}
        title={normsOpen?.title}
        norms={normsOpen?.norms}
      />
    </div>
  );
}
